import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getWithTokenAPI, postWithTokenAPI } from "../../util/Api";
import { SafeToast } from "../Toast/ShowToast";
import {
  Check,
  X,
  Clock,
  AlertCircle,
  Phone,
  Building2,
  Search,
  RefreshCw,
  ArrowRight,
  ShieldAlert,
  Calendar,
} from "lucide-react";

const PhoneApprovalComponent = () => {
  const { token } = useSelector((state) => state.auth);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pending"); // "Pending" | "Approved" | "Rejected" | "all"
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingCount, setPendingCount] = useState(0);

  // Modals state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, `/user/phone-update/requests?status=${activeTab}`);
      if (res?.statusCode === 200 && Array.isArray(res?.data)) {
        setRequests(res.data);
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error("Failed to fetch phone update requests:", err);
      SafeToast.error("Failed to load phone update requests");
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending count for tab badge
  const fetchPendingCount = async () => {
    try {
      const res = await getWithTokenAPI(token, "/user/phone-update/requests?status=Pending");
      if (res?.statusCode === 200 && Array.isArray(res?.data)) {
        setPendingCount(res.data.length);
      }
    } catch (err) {
      // silently ignore
    }
  };

  useEffect(() => {
    if (token) {
      fetchRequests();
      fetchPendingCount();
    }
  }, [token, activeTab]);

  const handleOpenApproveModal = (item) => {
    setSelectedRequest(item);
    setShowApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedRequest?._id) return;

    setIsProcessing(true);
    try {
      const res = await postWithTokenAPI(token, "/user/phone-update/review", {
        userId: selectedRequest._id,
        action: "approve",
      });

      if (res?.statusCode === 200 || !res?.error) {
        SafeToast.success(
          `Approved! Phone number updated to +91 ${selectedRequest.phoneUpdateRequest?.requestedPhone}`
        );
        setShowApproveModal(false);
        setSelectedRequest(null);
        fetchRequests();
        fetchPendingCount();
      } else {
        SafeToast.error(res?.message || "Failed to approve request");
      }
    } catch (err) {
      console.error("Approve error:", err);
      SafeToast.error("Failed to approve phone update");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenRejectModal = (item) => {
    setSelectedRequest(item);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRequest?._id) return;

    setIsProcessing(true);
    try {
      const res = await postWithTokenAPI(token, "/user/phone-update/review", {
        userId: selectedRequest._id,
        action: "reject",
        rejectionReason: rejectionReason.trim() || "Rejected by administrator",
      });

      if (res?.statusCode === 200 || !res?.error) {
        SafeToast.success("Request rejected");
        setShowRejectModal(false);
        setSelectedRequest(null);
        setRejectionReason("");
        fetchRequests();
        fetchPendingCount();
      } else {
        SafeToast.error(res?.message || "Failed to reject request");
      }
    } catch (err) {
      console.error("Reject error:", err);
      SafeToast.error("Failed to reject phone update");
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter requests by search term
  const filteredRequests = requests.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const instName = (item.nameofInstitution || "").toLowerCase();
    const currentPhone = (item.phoneNumber || "").toLowerCase();
    const reqPhone = (item.phoneUpdateRequest?.requestedPhone || "").toLowerCase();
    const email = (item.email || "").toLowerCase();
    const reason = (item.phoneUpdateRequest?.reason || "").toLowerCase();

    return (
      instName.includes(term) ||
      currentPhone.includes(term) ||
      reqPhone.includes(term) ||
      email.includes(term) ||
      reason.includes(term)
    );
  });

  return (
    <div style={{ padding: "4px 0" }}>
      {/* Top Filter and Search Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "14px",
          marginBottom: "20px",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "14px",
        }}
      >
        {/* Status Filter Tabs */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {[
            { key: "Pending", label: "Pending Requests", badge: pendingCount },
            { key: "Approved", label: "Approved" },
            { key: "Rejected", label: "Rejected" },
            { key: "all", label: "All History" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: activeTab === tab.key ? "#2563eb" : "#f1f5f9",
                color: activeTab === tab.key ? "#ffffff" : "#475569",
                fontWeight: 600,
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <span>{tab.label}</span>
              {typeof tab.badge === "number" && tab.badge > 0 && (
                <span
                  style={{
                    backgroundColor: activeTab === tab.key ? "#ffffff" : "#ef4444",
                    color: activeTab === tab.key ? "#2563eb" : "#ffffff",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "1px 7px",
                    borderRadius: "10px",
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search & Refresh Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              padding: "6px 12px",
              gap: "8px",
              width: "260px",
            }}
          >
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search institution / phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "13px",
                color: "#1e293b",
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  padding: 0,
                  display: "flex",
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            type="button"
            title="Refresh Requests"
            onClick={() => {
              fetchRequests();
              fetchPendingCount();
            }}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px", color: "#64748b" }}>
            <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "8px" }} />
            <div>Loading phone update requests...</div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "52px 20px" }}>
            <AlertCircle size={40} color="#94a3b8" style={{ marginBottom: "12px" }} />
            <h4 style={{ margin: "0 0 6px", color: "#1e293b", fontSize: "16px", fontWeight: "600" }}>
              No {activeTab === "all" ? "" : activeTab} Phone Requests Found
            </h4>
            <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
              {searchTerm
                ? "No requests match your current search query."
                : "There are currently no institution phone number update requests under this filter."}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    INSTITUTION NAME
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    CURRENT REGISTERED NUMBER
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    REQUESTED NEW NUMBER
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    REASON FOR REQUEST
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    REQUEST DATE & TIME
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", whiteSpace: "nowrap" }}>
                    STATUS
                  </th>
                  <th style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((item) => {
                  const req = item.phoneUpdateRequest || {};
                  return (
                    <tr
                      key={item._id}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        transition: "background 0.15s ease",
                      }}
                    >
                      {/* 1. Institution Name */}
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div
                            style={{
                              width: "38px",
                              height: "38px",
                              borderRadius: "8px",
                              background: "#eff6ff",
                              color: "#2563eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "700",
                              fontSize: "14px",
                              flexShrink: 0,
                            }}
                          >
                            <Building2 size={18} />
                          </div>
                          <div>
                            <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "14px" }}>
                              {item.nameofInstitution || "Unnamed Institution"}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                              {item.email || "No email"} {item.state ? `• ${item.state}` : ""}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* 2. Current Registered Number */}
                      <td style={{ padding: "14px 16px", fontSize: "13px", color: "#334155", fontWeight: "500" }}>
                        {item.phoneNumber ? (
                          <span>+91 {item.phoneNumber}</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </td>

                      {/* 3. Requested New Number */}
                      <td style={{ padding: "14px 16px" }}>
                        <span
                          style={{
                            fontWeight: "700",
                            color: "#1d4ed8",
                            background: "#dbeafe",
                            padding: "5px 11px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <Phone size={13} />
                          +91 {req.requestedPhone || "—"}
                        </span>
                      </td>

                      {/* 4. Reason for Request */}
                      <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569", maxWidth: "240px" }}>
                        {req.reason ? (
                          <div style={{ lineHeight: "1.4" }}>{req.reason}</div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No reason specified</span>
                        )}
                      </td>

                      {/* 5. Request Date & Time */}
                      <td style={{ padding: "14px 16px", fontSize: "13px" }}>
                        {req.requestedAt ? (
                          <div>
                            <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "13px" }}>
                              {new Date(req.requestedAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </div>
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                              {new Date(req.requestedAt).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </td>

                      {/* 6. Status */}
                      <td style={{ padding: "14px 16px" }}>
                        {req.status === "Pending" && (
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#b45309",
                              background: "#fef3c7",
                              padding: "4px 10px",
                              borderRadius: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Clock size={12} /> Pending
                          </span>
                        )}
                        {req.status === "Approved" && (
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#15803d",
                              background: "#dcfce7",
                              padding: "4px 10px",
                              borderRadius: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Check size={12} /> Approved
                          </span>
                        )}
                        {req.status === "Rejected" && (
                          <div>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#b91c1c",
                                background: "#fee2e2",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <X size={12} /> Rejected
                            </span>
                            {req.rejectionReason && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#ef4444",
                                  marginTop: "3px",
                                  maxWidth: "180px",
                                  lineHeight: "1.3",
                                }}
                              >
                                {req.rejectionReason}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* 7. Actions */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        {req.status === "Pending" ? (
                          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            {/* Approve Button */}
                            <button
                              type="button"
                              onClick={() => handleOpenApproveModal(item)}
                              disabled={isProcessing}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "7px 14px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#16a34a",
                                color: "#ffffff",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: isProcessing ? "not-allowed" : "pointer",
                                boxShadow: "0 1px 3px rgba(22, 163, 74, 0.2)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <Check size={14} /> Approve
                            </button>

                            {/* Reject Button */}
                            <button
                              type="button"
                              onClick={() => handleOpenRejectModal(item)}
                              disabled={isProcessing}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "7px 14px",
                                borderRadius: "6px",
                                border: "1px solid #fee2e2",
                                background: "#fef2f2",
                                color: "#dc2626",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: isProcessing ? "not-allowed" : "pointer",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <X size={14} /> Reject
                            </button>
                          </div>
                        ) : (
                          <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                            {req.reviewedAt
                              ? `Reviewed on ${new Date(req.reviewedAt).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                })}`
                              : "Resolved"}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approve Confirmation Modal */}
      {showApproveModal && selectedRequest && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "26px",
              maxWidth: "460px",
              width: "100%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "#dcfce7",
                  color: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
                  Approve Phone Number Update
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#64748b" }}>
                  Confirm changing registered institution phone number.
                </p>
              </div>
            </div>

            {/* Change summary card */}
            <div
              style={{
                background: "#f8fafc",
                borderRadius: "10px",
                padding: "16px",
                border: "1px solid #e2e8f0",
                marginBottom: "20px",
              }}
            >
              <div style={{ fontSize: "13px", color: "#475569", marginBottom: "10px" }}>
                Institution: <strong style={{ color: "#0f172a" }}>{selectedRequest.nameofInstitution}</strong>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  padding: "10px 12px",
                  background: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                }}
              >
                <div>
                  <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>Current</div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#334155" }}>
                    +91 {selectedRequest.phoneNumber || "None"}
                  </div>
                </div>

                <ArrowRight size={18} color="#2563eb" />

                <div>
                  <div style={{ fontSize: "11px", color: "#16a34a", textTransform: "uppercase", fontWeight: "600" }}>
                    New Number
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#16a34a" }}>
                    +91 {selectedRequest.phoneUpdateRequest?.requestedPhone}
                  </div>
                </div>
              </div>

              {selectedRequest.phoneUpdateRequest?.reason && (
                <div style={{ marginTop: "12px", fontSize: "12px", color: "#64748b" }}>
                  <strong>Reason given:</strong> {selectedRequest.phoneUpdateRequest.reason}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedRequest(null);
                }}
                disabled={isProcessing}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmApprove}
                disabled={isProcessing}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "9px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#16a34a",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 4px rgba(22, 163, 74, 0.3)",
                }}
              >
                {isProcessing ? "Approving..." : "Confirm & Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "26px",
              maxWidth: "460px",
              width: "100%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldAlert size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
                  Reject Phone Number Request
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#64748b" }}>
                  Rejecting request for <strong>{selectedRequest.nameofInstitution}</strong>
                </p>
              </div>
            </div>

            <form onSubmit={handleRejectSubmit}>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#334155",
                    marginBottom: "6px",
                  }}
                >
                  Reason for Rejection *
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Phone verification failed / Invalid institution documents"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    resize: "vertical",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedRequest(null);
                    setRejectionReason("");
                  }}
                  disabled={isProcessing}
                  style={{
                    padding: "9px 18px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: "9px 20px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#dc2626",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: isProcessing ? "not-allowed" : "pointer",
                    boxShadow: "0 2px 4px rgba(220, 38, 38, 0.3)",
                  }}
                >
                  {isProcessing ? "Rejecting..." : "Confirm Reject"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneApprovalComponent;
